import LegalLayout from "@/components/site/LegalLayout";

const Section = ({ title, children, n }) => (
  <section className="mt-12 first:mt-0">
    <div className="flex items-baseline gap-4">
      <span className="font-mono text-xs text-[#111111]/40 w-8">{n}</span>
      <h2 className="font-sora text-[22px] md:text-[26px] font-semibold tracking-tight text-[#111111]">
        {title}
      </h2>
    </div>
    <div className="mt-4 pl-12 space-y-4 font-manrope text-[16px] leading-[1.75] text-[#111111]/75">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  return (
    <LegalLayout
      kicker="Privacy Policy"
      title="Privacy Policy."
      updated="February 2026"
      path="/privacy"
    >
      <p
        data-testid="privacy-intro"
        className="font-manrope text-[18px] md:text-[19px] leading-[1.75] text-[#111111]/80 max-w-2xl"
      >
        This Privacy Policy explains how <strong>Vaymilo</strong>
        (&ldquo;Vaymilo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) collects,
        uses, stores, and shares personal information when you visit{" "}
        <a
          href="https://vaymilo.com"
          className="text-[#2563EB] hover:underline"
        >
          vaymilo.com
        </a>{" "}
        or buy a digital product from us. We collect the minimum we need to
        deliver products and respond to you — nothing more.
      </p>

      <Section n="01" title="Who we are">
        <p>
          <strong>Vaymilo</strong> is an independent digital product studio
          based in the United States (organised under the laws of the State of
          Delaware). We are the controller responsible for the processing of
          your personal information under this Policy.
        </p>
        <p>
          For all privacy queries, data access requests, or other concerns,
          please email{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>
          .
        </p>
      </Section>

      <Section n="02" title="Information we collect">
        <p>
          <strong>Information you provide directly</strong>
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Email address (when you subscribe to our list).</li>
          <li>Name, email, and message content (when you use the contact form).</li>
          <li>
            Quiz responses and first name (when you complete the {" "}
            <em>Find my kit</em> quiz and request a personalized starter PDF).
          </li>
          <li>
            Order information processed by our store and payment providers
            (we do not store payment card details on our servers).
          </li>
        </ul>
        <p className="pt-2">
          <strong>Information collected automatically</strong>
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            Standard log data such as IP address, browser type, device type,
            referrer URL, and timestamps.
          </li>
          <li>
            Aggregated, non-identifying usage analytics (page views, clicks,
            session duration). We do not run third-party advertising trackers.
          </li>
        </ul>
      </Section>

      <Section n="03" title="Why we process your data">
        <p>
          We process your personal information for the following purposes, on
          the lawful basis of your consent (or, where applicable, our
          legitimate interest in operating the website and the contract we
          enter into with you when you make a purchase):
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>To deliver any digital product or kit you have purchased.</li>
          <li>To reply to messages submitted through the contact form.</li>
          <li>
            To send newsletters, free tools, product updates, and occasional
            launch discounts — only after you opt in.
          </li>
          <li>To detect, prevent, and respond to fraud or abuse.</li>
          <li>To improve the site through aggregate analytics.</li>
          <li>
            To comply with applicable laws, including U.S. tax and recordkeeping
            obligations.
          </li>
        </ul>
      </Section>

      <Section n="04" title="Sharing and disclosure">
        <p>
          We do <strong>not</strong> sell your personal information. We share
          it only with service providers strictly necessary to operate Vaymilo:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Cloud hosting and database providers (to store your data securely).</li>
          <li>Email delivery providers (to send replies and newsletters).</li>
          <li>Privacy-respecting analytics providers, to understand site usage.</li>
          <li>Payment processors used at checkout.</li>
          <li>
            Government or law enforcement authorities, if required by valid
            legal process.
          </li>
        </ul>
      </Section>

      <Section n="05" title="International transfers">
        <p>
          Vaymilo is based in the United States. If you access the Site from
          outside the U.S., your information will be transferred to, stored,
          and processed in the U.S. Where required by law, we rely on
          appropriate contractual safeguards (including Standard Contractual
          Clauses) and our providers&rsquo; own compliance frameworks to
          protect those transfers.
        </p>
      </Section>

      <Section n="06" title="Data retention">
        <p>
          We keep your personal information only for as long as needed for the
          purposes set out above, or as required by law:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Email subscribers: until you unsubscribe.</li>
          <li>Contact-form messages: up to 24 months from receipt.</li>
          <li>
            Order and tax records: as required by applicable tax law (typically
            up to 7 years).
          </li>
        </ul>
      </Section>

      <Section n="07" title="Your rights">
        <p>
          Depending on where you live, you may have the right to:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Access the personal information we hold about you.</li>
          <li>Correct inaccurate or incomplete information.</li>
          <li>Delete your personal information, subject to legal retention rules.</li>
          <li>Withdraw your consent at any time.</li>
          <li>
            Opt out of marketing emails (every newsletter we send includes a
            one-click unsubscribe link).
          </li>
          <li>
            If you are in the European Economic Area, the United Kingdom, or
            California, exercise the additional rights granted to you under
            GDPR, UK GDPR, or the CCPA / CPRA respectively.
          </li>
        </ul>
        <p>
          To exercise any right, email{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>{" "}
          with the subject line &ldquo;Data Request&rdquo;. We respond within
          30 days.
        </p>
      </Section>

      <Section n="08" title="Security">
        <p>
          We use industry-standard administrative, technical, and physical
          measures to protect your data — including encrypted transport (HTTPS),
          access controls, and secure password hashing for internal accounts.
          No system is perfectly secure; if you believe your data has been
          compromised, please contact us immediately.
        </p>
      </Section>

      <Section n="09" title="Cookies">
        <p>
          We use a small number of essential cookies to operate the site and
          remember your preferences. Aggregated analytics cookies do not
          contain advertising identifiers. You can clear cookies through your
          browser settings at any time.
        </p>
      </Section>

      <Section n="10" title="Children">
        <p>
          Vaymilo is not intended for use by children under 16. We do not
          knowingly collect personal information from minors. If you believe a
          minor has provided us information, please email us so we can delete
          it.
        </p>
      </Section>

      <Section n="11" title="Changes to this Policy">
        <p>
          We may update this Policy from time to time. Material changes will be
          posted here with an updated &ldquo;Last updated&rdquo; date. Your
          continued use of the site after changes constitutes acceptance of the
          revised Policy.
        </p>
      </Section>

      <Section n="12" title="Contact">
        <p>
          Questions about this Policy or how we handle your data? Email{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
};

export default Privacy;
